import type {RecurringEvent} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.types';
import type {TimeZone} from 'timezones-list';
import type {RegistrationMode, EventWizardProgressStep} from '@Components/event-wizard/event-wizard.types';
import type {TicketingConfig, TicketingFeeParams} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';

export interface PreviewProps {
  isMobileScreenSize?: boolean;
}

export const EVENT_PAGE_MAIN_CONTAINER_ID = 'event-page';

export const PREVIEW_IFRAME_ID = 'previewIframeID';

// Cover Photo
export const COVER_PHOTO_CONTAINER_ID = 'coverPhotoContainerID';
export const COVER_PHOTO_ID = 'coverPhotoID';
export const COVER_PHOTO_PLACEHOLDER_ICON_ID = 'coverPhotoIconPlaceholderID';
export const COVER_PHOTO_BACKGROUND_ID = 'coverPhotoBgID';

// Title
export const TITLE_ID = 'titleID';

// Description
export const DESCRIPTION_ID = 'descriptionID';

// Venue Summary
export const VENUE_SUMMARY_TITLE = 'venueSummaryTitle';
export const VENUE_SUMMARY_SUBTITLE = 'venueSummarySubtitle';
export const VENUE_ICON_EMPTY_ID = 'venueIconEmptyID';
export const VENUE_ICON_MAP_ID = 'venueIconMapID';
export const VENUE_ICON_GOOGLE_MEET_ID = 'venueIconGoogleMeetID';
export const VENUE_ICON_ZOOM_ID = 'venueIconZoomID';
export const VENUE_ICON_ONLINE_ID = 'venueIconOnlineID';
export const VENUE_ICON_ONLINE_NON_URL_ID = 'venueIconOnlineNonURLID';

// Venue
export const VENUE_MAP_ID = 'venueMapID';
export const VENUE_MAP_LOCATION_ID = 'venueMapLocationID';
export const VENUE_MAP_NAME_ID = 'venueMapNameID';
export const VENUE_MAP_ADDRESS_ID = 'venueMapAddressID';
export const VENUE_MAP_ADDRESS_DIRECTIONS_ID = 'venueMapAddressDirectionsID';

export const VENUE_ONLINE_ID = 'venueOnlineID';
export const VENUE_ONLINE_NON_URL_ID = 'venueOnlineNonURLID';
export const VENUE_ONLINE_NON_URL_TEXT_ID = 'venueOnlineNonURLTextID';
export const VENUE_ONLINE_GOOGLE_MEET_ID = 'venueOnlineGoogleMeetID';
export const VENUE_ONLINE_ZOOM_ID = 'venueOnlineZoomID';

// Date Time
export const DATE_TIME_EMPTY_ID = 'dateTimeEmptyID';
export const DATE_TIME_SINGLE_DAY_ID = 'dateTimeSingleDayID';
export const DATE_TIME_MULTIDAY_ID = 'dateTimeMultidayID';
export const DATE_TIME_RECURRING_DAILY_ID = 'dateTimeRecurDailyID';
export const DATE_TIME_RECURRING_WEEKLY_SINGLE_ID = 'dateTimeRecurWeeklySingleID';
export const DATE_TIME_RECURRING_WEEKLY_MULTI_ID = 'dateTimeRecurWeeklyMultiID';
export const DATE_TIME_RECURRING_MONTHLY_ID = 'dateTimeRecurMonthlyID';

// Preview shortcuts: on step 1, clicking a part of the preview takes the user to the field that sets it.
/** Styled in event-page.scss; the wizard adds it on step 1 only, so a live event page never has it. */
export const PREVIEW_SHORTCUT_CLASS = 'wizard-preview-shortcut';
export const PREVIEW_SHORTCUT_CLICKED_EVENT = 'eventWizardPreviewShortcutClicked';
/** The events the wizard's outside-click handlers listen for (useOutsideClick dismisses on one or the other). */
export const OUTSIDE_CLICK_EVENT_TYPES = ['mousedown', 'click'] as const;

export enum PreviewShortcutTarget {
  COVER = 'cover',
  TITLE = 'title',
  DATE_TIME = 'date-time',
}

export interface PreviewShortcutClickedDetail {
  target: PreviewShortcutTarget;
}

export interface PreviewShortcut {
  target: PreviewShortcutTarget;
  /** The preview elements that lead to the target; the event page has one of each per layout. */
  elementClasses: string[];
}

/** Each date and time layout the preview can show, one visible at a time. */
export const DATE_TIME_PREVIEW_CLASSES = [
  DATE_TIME_EMPTY_ID,
  DATE_TIME_SINGLE_DAY_ID,
  DATE_TIME_MULTIDAY_ID,
  DATE_TIME_RECURRING_DAILY_ID,
  DATE_TIME_RECURRING_WEEKLY_SINGLE_ID,
  DATE_TIME_RECURRING_WEEKLY_MULTI_ID,
  DATE_TIME_RECURRING_MONTHLY_ID,
];

export const PREVIEW_SHORTCUTS: PreviewShortcut[] = [
  {target: PreviewShortcutTarget.COVER, elementClasses: [COVER_PHOTO_CONTAINER_ID]},
  {target: PreviewShortcutTarget.TITLE, elementClasses: [TITLE_ID]},
  {target: PreviewShortcutTarget.DATE_TIME, elementClasses: DATE_TIME_PREVIEW_CLASSES},
];

export const DATE_TIME_ICON_START_MONTH_ID = 'dateTimeIconStartMonthID';
export const DATE_TIME_ICON_START_DATE_ID = 'dateTimeIconStartDateID';
export const DATE_TIME_START_TITLE_ID = 'dateTimeStartTitleID';
export const DATE_TIME_MOBILE_START_TITLE_ID = 'dateTimeMobileStartTitleID';
export const DATE_TIME_START_SUBTITLE_ID = 'dateTimeStartSubtitleID';
export const DATE_TIME_END_TITLE_ID = 'dateTimeEndTitleID';
export const DATE_TIME_MOBILE_END_TITLE_ID = 'dateTimeMobileEndTitleID';
export const DATE_TIME_END_SUBTITLE_ID = 'dateTimeEndSubtitleID';
export const DATE_TIME_TIMEZONE_ID = 'dateTimeTimeZoneID';

// Countdown
export const CUSTOM_EVENT_COUNTDOWN_WIZARD_UPDATED = 'countdown_updated_wizard';
export const COUNTDOWN_ID = 'countdownID';

// Organiser
export const ORGANISER_PROFILE_PICTURE = 'organiserProfilePictureID';
export const ORGANISER_NAME = 'organiserNameID';
export const ORGANISER_EMAIL = 'organiserEmailID';
export const ORGANISER_PHONE = 'organiserPhoneID';
export const ORGANISER_SOCIAL_CONTAINER = 'organiserSocialContainerID';
export const ORGANISER_SOCIAL_FACEBOOK = 'organiserSocialFacebookID';
export const ORGANISER_SOCIAL_INSTAGRAM = 'organiserSocialInstagramID';
export const ORGANISER_SOCIAL_LINKEDIN = 'organiserSocialLinkedinID';
export const ORGANISER_SOCIAL_X = 'organiserSocialXID';
export const ORGANISER_SOCIAL_PINTEREST = 'organiserSocialPinterestID';
export const ORGANISER_SOCIAL_YOUTUBE = 'organiserSocialYoutubeID';
export const ORGANISER_SOCIAL_TIKTOK = 'organiserSocialTiktokID';

// Registration form
export const REGISTRATION_CTA_ID = 'registrationCTAID';
export const CUSTOM_EVENT_WIZARD_CHECKOUT_SYNC = 'wizard_checkout_preview_sync';

export interface WizardCheckoutPreviewSyncArgs {
  config: TicketingConfig;
  mode: RegistrationMode;
  step: EventWizardProgressStep;
  eventTitle: string;
  startDateTime: Date;
  coverPhotoUrl?: string;
  /** Server-resolved fee rates, or null until they load — the preview total falls back to the ticket price meanwhile. */
  ticketingFees: TicketingFeeParams | null;
}

// Watermark
export const WATERMARK_ID = 'watermarkID';

// Styling
export const STYLE_LIGHTER_BG_COLOR_ID = 'styleLighterBgColorID';
export const STYLE_LIGHT_BG_COLOR_ID = 'styleLightBgColorID';
export const STYLE_COLOR_ID = 'styleColorID';

/**
 * The inline hover handlers `EventStyleVO::getButtonAttributes` writes for Mode A. The themed path
 * must not carry them — see `removeInlineHoverHandlers` in `preview.helper`.
 */
export const INLINE_HOVER_ATTRIBUTES = ['onmouseover', 'onmouseout', 'onfocus'] as const;
export const STYLE_DARK_TEXT_COLOR_ID = 'styleDarkColorID';
// Modifier toggled on the page container when a custom background is active; the
// public PHP page (Event.php) emits the same class, and event SCSS keys off it.
export const EVENT_PAGE_CUSTOM_BG_CLASS = '-custom-bg';


export interface EventPreviewDateTime {
  startDateTime: Date;
  endDateTime: Date;
  recurringPattern?: RecurringEvent;
  timeZone: TimeZone;
}
